import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Eye, Truck, Trash2, LayoutDashboard, Store, Wrench, User, Users, Radio, MapPin, History, Star } from "lucide-react";
import { toast } from "sonner";
import PhoneInput from "@/components/PhoneInput";
import MapView from "@/components/MapView";

const STATUS = {
  pending: { label: "Onay Bekliyor", color: "bg-amber-500 text-white" },
  approved: { label: "Yayında", color: "bg-[hsl(var(--success))] text-white" },
  rejected: { label: "Reddedildi", color: "bg-destructive text-white" },
  suspended: { label: "Askıda", color: "bg-slate-500 text-white" },
};

const VEHICLE_TYPES = [
  "Oto Kurtarıcı", "Kayar Kasa Kurtarıcı", "Çift Katlı Araç Taşıyıcı",
  "Ağır Vasıta Kurtarıcı", "Oto Vinç", "Lowbed", "Çekici + Dorse",
  "Kamyon", "Servis Aracı", "Mobil Lastik Aracı", "Diğer",
];

const EMPTY_V = { name: "", vehicle_type: "Oto Kurtarıcı", plate: "", photo_url: "", description: "", capacity: "", payload_types: [], service_category_ids: [], driver_id: null, is_active: true, is_busy: false };
const EMPTY_D = { name: "", email: "", phone: "", password: "", is_active: true };

export default function OwnerDashboard() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [businesses, setBusinesses] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [cats, setCats] = useState([]);
  const [selectedBiz, setSelectedBiz] = useState("");
  const [editingV, setEditingV] = useState(null);
  const [newV, setNewV] = useState(EMPTY_V);
  const [newD, setNewD] = useState(EMPTY_D);
  const [pwd, setPwd] = useState({ current: "", next: "" });
  const [fleet, setFleet] = useState([]);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/giris"); return; }
    api.get("/categories").then(({ data }) => setCats(data));
    api.get("/my/businesses").then(({ data }) => {
      setBusinesses(data);
      if (data[0] && !selectedBiz) setSelectedBiz(data[0].id);
    });
    api.get("/my/drivers").then(({ data }) => setDrivers(data));
  }, [user, loading, nav]); // eslint-disable-line

  useEffect(() => {
    if (selectedBiz) api.get("/my/vehicles", { params: { business_id: selectedBiz } }).then(({ data }) => setVehicles(data));
  }, [selectedBiz]);

  const reloadVehicles = () => selectedBiz && api.get("/my/vehicles", { params: { business_id: selectedBiz } }).then(({ data }) => setVehicles(data));
  const reloadDrivers = () => api.get("/my/drivers").then(({ data }) => setDrivers(data));
  const reloadFleet = () => api.get("/my/fleet-live").then(({ data }) => setFleet(data));

  useEffect(() => { if (tab === "fleet") { reloadFleet(); const t = setInterval(reloadFleet, 15000); return () => clearInterval(t); } }, [tab]);
  useEffect(() => { if (tab === "history") { api.get("/provider/help-history").then(({ data }) => setHistory(data)); } }, [tab]);

  const catName = (id) => cats.find((c) => c.id === id)?.name || "—";

  // Drivers
  const addDriver = async (e) => {
    e.preventDefault();
    if (!newD.email || !newD.password || !newD.name) return toast.error("Ad, e-posta ve şifre zorunlu");
    try {
      await api.post("/my/drivers", newD);
      setNewD(EMPTY_D);
      toast.success("Şoför eklendi");
      reloadDrivers();
    } catch (err) { toast.error(err.response?.data?.detail || "Ekleme başarısız"); }
  };
  const delDriver = async (did) => {
    if (!window.confirm("Bu şoförü silmek istiyor musunuz?")) return;
    await api.delete(`/my/drivers/${did}`);
    reloadDrivers(); reloadVehicles();
  };
  const toggleDriverActive = async (d) => {
    await api.put(`/my/drivers/${d.id}`, { is_active: !d.is_active });
    reloadDrivers();
  };

  // Vehicles
  const addVehicle = async (e) => {
    e.preventDefault();
    if (!selectedBiz) return toast.error("Önce bir işletme seçin");
    if (!newV.name) return toast.error("Araç adı gerekli");
    try {
      await api.post("/my/vehicles", newV, { params: { business_id: selectedBiz } });
      setNewV(EMPTY_V);
      toast.success("Araç eklendi");
      reloadVehicles();
    } catch (err) { toast.error(err.response?.data?.detail || "Ekleme başarısız"); }
  };
  const updateVehicle = async () => {
    const { id: vid, ...body } = editingV;
    try {
      await api.put(`/my/vehicles/${vid}`, body);
      setEditingV(null);
      toast.success("Araç güncellendi");
      reloadVehicles();
    } catch (err) { toast.error(err.response?.data?.detail || "Güncelleme başarısız"); }
  };
  const delVehicle = async (vid) => {
    if (!window.confirm("Bu aracı silmek istiyor musunuz?")) return;
    await api.delete(`/my/vehicles/${vid}`); reloadVehicles();
  };

  const toggleVehicleCap = (vid, capId, target) => {
    if (target === "new") {
      setNewV((s) => ({
        ...s,
        service_category_ids: s.service_category_ids.includes(capId)
          ? s.service_category_ids.filter((x) => x !== capId)
          : [...s.service_category_ids, capId],
      }));
    } else {
      setEditingV((s) => ({
        ...s,
        service_category_ids: (s.service_category_ids || []).includes(capId)
          ? s.service_category_ids.filter((x) => x !== capId)
          : [...(s.service_category_ids || []), capId],
      }));
    }
  };

  const currentBiz = businesses.find((b) => b.id === selectedBiz);
  const bizCatIds = currentBiz?.category_ids || [];
  const bizCats = cats.filter((c) => bizCatIds.includes(c.id));

  const changePassword = async (e) => {
    e.preventDefault();
    if (!pwd.current || !pwd.next) return toast.error("Alanları doldurun");
    if (pwd.next.length < 6) return toast.error("Yeni şifre en az 6 karakter");
    try {
      await api.post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
      toast.success("Şifre değiştirildi");
      setPwd({ current: "", next: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Başarısız"); }
  };

  // Fleet map center
  const fleetOnline = fleet.filter((v) => v.is_online && v.last_location);
  const mapCenter = fleetOnline[0] ? [fleetOnline[0].last_location.latitude, fleetOnline[0].last_location.longitude] : [39, 35];

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Hizmet Sağlayıcı Paneli</h1>
          <p className="text-sm text-muted-foreground">Hoş geldiniz, {user?.name}</p>
        </div>
        <Button data-testid="new-business-btn" asChild className="rounded-full press bg-primary text-[hsl(var(--brand-black))] hover:bg-primary/90 font-bold">
          <Link to="/isletme-ekle"><Plus className="w-4 h-4 mr-1" /> Yeni İşletme</Link>
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger data-testid="tab-overview" value="overview"><LayoutDashboard className="w-4 h-4 mr-1" /> Genel Bakış</TabsTrigger>
          <TabsTrigger data-testid="tab-mybusiness" value="mybusiness"><Store className="w-4 h-4 mr-1" /> İşletmem</TabsTrigger>
          <TabsTrigger data-testid="tab-vehicles" value="vehicles"><Truck className="w-4 h-4 mr-1" /> Araçlarım</TabsTrigger>
          <TabsTrigger data-testid="tab-drivers" value="drivers"><Users className="w-4 h-4 mr-1" /> Şoförlerim</TabsTrigger>
          <TabsTrigger data-testid="tab-fleet" value="fleet"><Radio className="w-4 h-4 mr-1" /> Canlı Filo</TabsTrigger>
          <TabsTrigger data-testid="tab-services" value="services"><Wrench className="w-4 h-4 mr-1" /> Hizmetlerim</TabsTrigger>
          <TabsTrigger data-testid="tab-history" value="history"><History className="w-4 h-4 mr-1" /> Görev Geçmişi</TabsTrigger>
          <TabsTrigger data-testid="tab-membership" value="membership"><Wrench className="w-4 h-4 mr-1" /> Üyeliğim / Ödeme</TabsTrigger>
          <TabsTrigger data-testid="tab-account" value="account"><User className="w-4 h-4 mr-1" /> Hesap</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border p-4"><div className="text-xs text-muted-foreground">İŞLETME</div><div className="text-2xl font-bold">{businesses.length}</div></div>
            <div className="rounded-2xl border p-4"><div className="text-xs text-muted-foreground">ARAÇ</div><div className="text-2xl font-bold">{vehicles.length}</div></div>
            <div className="rounded-2xl border p-4"><div className="text-xs text-muted-foreground">ŞOFÖR</div><div className="text-2xl font-bold">{drivers.length}</div></div>
          </div>
          <ProviderRevenueCards />
        </TabsContent>

        <TabsContent value="mybusiness">
          <div className="grid gap-3">
            {businesses.map((b) => (
              <div key={b.id} className="rounded-2xl border p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold truncate">{b.name}</h3>
                    <Badge className={STATUS[b.status]?.color}>{STATUS[b.status]?.label}</Badge>
                    {b.is_24_7 && <Badge className="bg-primary text-[hsl(var(--brand-black))]">7/24</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{b.district}, {b.province} · Özel Tel: {b.provider_private_phone || "—"}</p>
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="outline" asChild data-testid={`view-${b.id}`}><Link to={`/firma/${b.slug || b.id}`}><Eye className="w-4 h-4" /></Link></Button>
                  <Button size="icon" variant="outline" asChild data-testid={`edit-${b.id}`}><Link to={`/isletme-duzenle/${b.id}`}><Pencil className="w-4 h-4" /></Link></Button>
                </div>
              </div>
            ))}
            {businesses.length === 0 && <p className="text-muted-foreground">Henüz işletme yok.</p>}
          </div>
        </TabsContent>

        <TabsContent value="vehicles">
          {businesses.length === 0 ? (
            <p className="text-muted-foreground">Önce bir işletme oluşturun.</p>
          ) : (
            <>
              <div className="mb-3">
                <Label>İşletme</Label>
                <Select value={selectedBiz} onValueChange={setSelectedBiz}>
                  <SelectTrigger data-testid="v-biz-select" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{businesses.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <form onSubmit={addVehicle} className="rounded-2xl border p-4 mb-4 space-y-2">
                <h3 className="font-semibold">Yeni Araç</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div><Label>Araç Adı</Label><Input data-testid="v-name" value={newV.name} onChange={(e) => setNewV({ ...newV, name: e.target.value })} className="mt-1" /></div>
                  <div>
                    <Label>Araç Tipi</Label>
                    <Select value={newV.vehicle_type} onValueChange={(v) => setNewV({ ...newV, vehicle_type: v })}>
                      <SelectTrigger data-testid="v-type" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{VEHICLE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Plaka (özel)</Label><Input data-testid="v-plate" value={newV.plate} onChange={(e) => setNewV({ ...newV, plate: e.target.value })} className="mt-1" /></div>
                  <div><Label>Kapasite</Label><Input data-testid="v-capacity" value={newV.capacity} onChange={(e) => setNewV({ ...newV, capacity: e.target.value })} placeholder="Örn: 40 ton" className="mt-1" /></div>
                </div>
                <div>
                  <Label>Atanmış Şoför</Label>
                  <Select value={newV.driver_id || "none"} onValueChange={(v) => setNewV({ ...newV, driver_id: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="v-driver" className="mt-1"><SelectValue placeholder="Şoför seçin" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Atanmamış —</SelectItem>
                      {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} ({d.email})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {bizCats.length > 0 && (
                  <div>
                    <Label>Araç Bazlı Hizmet Yeteneği (isteğe bağlı)</Label>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {bizCats.map((c) => (
                        <label key={c.id} className={`text-xs px-2 py-1 rounded-full border cursor-pointer ${newV.service_category_ids.includes(c.id) ? "bg-primary text-[hsl(var(--brand-black))] border-primary" : "bg-card"}`}>
                          <Checkbox className="hidden" checked={newV.service_category_ids.includes(c.id)} onCheckedChange={() => toggleVehicleCap(null, c.id, "new")} />
                          <span onClick={() => toggleVehicleCap(null, c.id, "new")}>{c.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <div><Label>Açıklama</Label><Textarea value={newV.description} onChange={(e) => setNewV({ ...newV, description: e.target.value })} rows={2} className="mt-1" /></div>
                <Button data-testid="v-add-btn" type="submit" className="rounded-full">Araç Ekle</Button>
              </form>

              <div className="grid gap-2">
                {vehicles.map((v) => {
                  const drv = drivers.find((d) => d.id === v.driver_id);
                  return (
                    <div key={v.id} className="rounded-2xl border p-4">
                      {editingV?.id === v.id ? (
                        <div className="space-y-2">
                          <Input value={editingV.name} onChange={(e) => setEditingV({ ...editingV, name: e.target.value })} />
                          <Select value={editingV.vehicle_type} onValueChange={(x) => setEditingV({ ...editingV, vehicle_type: x })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{VEHICLE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                          </Select>
                          <Input value={editingV.plate || ""} onChange={(e) => setEditingV({ ...editingV, plate: e.target.value })} placeholder="Plaka" />
                          <Input value={editingV.capacity || ""} onChange={(e) => setEditingV({ ...editingV, capacity: e.target.value })} placeholder="Kapasite" />
                          <Select value={editingV.driver_id || "none"} onValueChange={(x) => setEditingV({ ...editingV, driver_id: x === "none" ? null : x })}>
                            <SelectTrigger><SelectValue placeholder="Şoför" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">— Atanmamış —</SelectItem>
                              {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} ({d.email})</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {bizCats.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {bizCats.map((c) => (
                                <button type="button" key={c.id} onClick={() => toggleVehicleCap(v.id, c.id, "edit")}
                                  className={`text-xs px-2 py-1 rounded-full border ${(editingV.service_category_ids || []).includes(c.id) ? "bg-primary text-[hsl(var(--brand-black))] border-primary" : "bg-card"}`}>
                                  {c.name}
                                </button>
                              ))}
                            </div>
                          )}
                          <div className="flex gap-4">
                            <label className="flex items-center gap-1 text-sm"><Switch checked={editingV.is_active} onCheckedChange={(c) => setEditingV({ ...editingV, is_active: c })} /> Aktif</label>
                            <label className="flex items-center gap-1 text-sm"><Switch checked={editingV.is_busy} onCheckedChange={(c) => setEditingV({ ...editingV, is_busy: c })} /> Meşgul</label>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={updateVehicle} className="rounded-full">Kaydet</Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingV(null)} className="rounded-full">İptal</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-semibold">{v.name}</div>
                            <div className="text-xs text-muted-foreground">{v.vehicle_type}{v.plate ? ` · ${v.plate}` : ""}{v.capacity ? ` · ${v.capacity}` : ""}</div>
                            <div className="text-xs mt-0.5">Şoför: <b>{drv?.name || "Atanmamış"}</b></div>
                            {v.service_category_ids?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {v.service_category_ids.map((cid) => <Badge key={cid} variant="secondary" className="text-xs">{catName(cid)}</Badge>)}
                              </div>
                            )}
                            <div className="flex gap-1 mt-1">
                              {v.is_active ? <Badge className="bg-[hsl(var(--success))] text-white">Aktif</Badge> : <Badge variant="secondary">Pasif</Badge>}
                              {v.is_online && <Badge className="bg-primary text-[hsl(var(--brand-black))]">HATTA</Badge>}
                              {v.is_busy && <Badge className="bg-amber-500 text-white">Meşgul</Badge>}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button size="icon" variant="outline" onClick={() => setEditingV(v)}><Pencil className="w-4 h-4" /></Button>
                            <Button size="icon" variant="outline" onClick={() => delVehicle(v.id)}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {vehicles.length === 0 && <p className="text-muted-foreground">Bu işletmeye ait araç yok.</p>}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="drivers">
          <div className="rounded-2xl border p-4 mb-4 bg-primary/5">
            <p className="text-sm">Şoför hesapları firmanıza bağlı <b>alt kullanıcılardır</b>. Her şoför kendi telefonundan giriş yapıp yalnızca kendisine atanmış aracın canlı konumunu paylaşabilir.</p>
          </div>
          <form onSubmit={addDriver} className="rounded-2xl border p-4 mb-4 space-y-2">
            <h3 className="font-semibold">Yeni Şoför</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div><Label>Ad Soyad</Label><Input data-testid="d-name" value={newD.name} onChange={(e) => setNewD({ ...newD, name: e.target.value })} className="mt-1" /></div>
              <div><Label>E-posta (giriş)</Label><Input data-testid="d-email" type="email" value={newD.email} onChange={(e) => setNewD({ ...newD, email: e.target.value })} className="mt-1" /></div>
              <div><Label>Telefon</Label><PhoneInput testid="d-phone" value={newD.phone} onChange={(v) => setNewD({ ...newD, phone: v })} /></div>
              <div><Label>Giriş Şifresi</Label><Input data-testid="d-password" type="password" minLength={6} value={newD.password} onChange={(e) => setNewD({ ...newD, password: e.target.value })} className="mt-1" /></div>
            </div>
            <Button data-testid="d-add-btn" type="submit" className="rounded-full">Şoför Ekle</Button>
          </form>
          <div className="grid gap-2">
            {drivers.map((d) => {
              const vs = vehicles.filter((v) => v.driver_id === d.id);
              return (
                <div key={d.id} className="rounded-2xl border p-4 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="font-semibold">{d.name}</div>
                    <div className="text-xs text-muted-foreground">{d.email} · {d.phone || "—"}</div>
                    <div className="text-xs mt-1">Atanmış araç: <b>{vs.map((v) => v.name).join(", ") || "Yok"}</b></div>
                    <div className="mt-1">{d.is_active ? <Badge className="bg-[hsl(var(--success))] text-white">Aktif</Badge> : <Badge variant="secondary">Pasif</Badge>}</div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="outline" onClick={() => toggleDriverActive(d)} data-testid={`d-toggle-${d.id}`}><Radio className="w-4 h-4" /></Button>
                    <Button size="icon" variant="outline" onClick={() => delDriver(d.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              );
            })}
            {drivers.length === 0 && <p className="text-muted-foreground">Henüz şoför yok.</p>}
          </div>
        </TabsContent>

        <TabsContent value="fleet">
          <div className="mb-3 text-sm text-muted-foreground">Sadece kendi filonuzun canlı konumları. 15 saniyede bir yenilenir.</div>
          <div className="rounded-2xl overflow-hidden border h-[420px]">
            <MapView
              center={mapCenter}
              zoom={fleetOnline[0] ? 11 : 6}
              markers={fleetOnline.map((v) => ({ id: v.id, latitude: v.last_location.latitude, longitude: v.last_location.longitude, label: `${v.name}${v.plate ? " (" + v.plate + ")" : ""}` }))}
              className="h-full"
            />
          </div>
          <div className="mt-3 grid gap-2">
            {fleet.map((v) => {
              const drv = drivers.find((d) => d.id === v.driver_id);
              return (
                <div key={v.id} className="rounded-xl border p-3 flex items-center justify-between">
                  <div>
                    <div className="font-semibold">{v.name} {v.plate ? <span className="text-xs text-muted-foreground">· {v.plate}</span> : null}</div>
                    <div className="text-xs text-muted-foreground">Şoför: {drv?.name || "—"} · {v.vehicle_type}</div>
                    {v.last_location_at && <div className="text-xs">Son konum: {new Date(v.last_location_at).toLocaleString("tr-TR")}</div>}
                  </div>
                  {v.is_online ? <Badge className="bg-[hsl(var(--success))] text-white"><Radio className="w-3 h-3 mr-1" /> HATTA</Badge> : <Badge variant="secondary">PASİF</Badge>}
                </div>
              );
            })}
            {fleet.length === 0 && <p className="text-muted-foreground text-sm">Filoda araç yok.</p>}
          </div>
        </TabsContent>

        <TabsContent value="services">
          {currentBiz ? (
            <div className="rounded-2xl border p-4">
              <div className="mb-2 font-semibold">{currentBiz.name}</div>
              <div className="text-xs text-muted-foreground mb-2">Sunulan hizmet kategorileri:</div>
              <div className="flex flex-wrap gap-1.5">
                {bizCats.length === 0 && <span className="text-muted-foreground text-sm">Kategori seçilmemiş</span>}
                {bizCats.map((c) => <Badge key={c.id} variant="secondary" data-testid={`svc-badge-${c.slug}`}>{c.name}</Badge>)}
              </div>
              <Button asChild size="sm" variant="outline" className="mt-3 rounded-full">
                <Link to={`/isletme-duzenle/${currentBiz.id}`}>Hizmetleri Düzenle</Link>
              </Button>
            </div>
          ) : <p className="text-muted-foreground">Önce bir işletme oluşturun.</p>}
        </TabsContent>

        <TabsContent value="history">
          <div className="grid gap-2">
            {history.length === 0 && <p className="text-muted-foreground text-sm">Henüz kayıt yok.</p>}
            {history.map((h) => {
              const STATUS_LBL = { pending: "Bekliyor", accepted: "Kabul Edildi", driver_arriving: "Kurtarıcı Yolda", arrived: "Kurtarıcı Geldi", completed: "Tamamlandı", rejected: "Reddedildi", cancelled: "İptal Edildi" };
              return (
                <div key={h.id} className="rounded-2xl border p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`owner-hist-${h.id}`}>
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground">{new Date(h.created_at).toLocaleString("tr-TR")}</div>
                    <div className="font-semibold">{h.request_type} · {h.customer_vehicle_type}</div>
                    <div className="text-xs">Şoför: <b>{h.driver_name || "—"}</b> · Araç: <b>{h.vehicle_name || "—"}</b> · Müşteri: <b>{h.customer_name || "—"}</b></div>
                    {h.agreed_price != null && (
                      <div className="text-sm text-primary font-semibold mt-1" data-testid={`owner-hist-price-${h.id}`}>
                        Anlaşılan Fiyat: {Number(h.agreed_price).toLocaleString("tr-TR")} ₺
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge>{STATUS_LBL[h.status] || h.status}</Badge>
                    {h.review && (
                      <span className="inline-flex items-center gap-1 text-primary text-sm" data-testid={`owner-hist-review-${h.id}`}>
                        {Array.from({ length: h.review.rating }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-current" />)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="membership">
          <MembershipTab />
        </TabsContent>

        <TabsContent value="account">
          <div className="rounded-2xl border p-4 mb-3">
            <div className="text-sm"><span className="text-muted-foreground">Ad Soyad:</span> <b>{user?.name}</b></div>
            <div className="text-sm"><span className="text-muted-foreground">E-posta:</span> {user?.email}</div>
            <div className="text-sm"><span className="text-muted-foreground">Rol:</span> Firma Sahibi (Ana Hesap)</div>
            <div className="text-sm"><span className="text-muted-foreground">Şoför alt hesapları:</span> {drivers.length}</div>
            <div className="text-sm mt-2 text-muted-foreground">Firma sahibi hesabı ücretsizdir.</div>
          </div>
          <form onSubmit={changePassword} className="rounded-2xl border p-4 space-y-2">
            <h3 className="font-semibold">Şifre Değiştir</h3>
            <div><Label>Mevcut Şifre</Label><Input type="password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} className="mt-1" data-testid="pwd-current" /></div>
            <div><Label>Yeni Şifre (min 6)</Label><Input type="password" minLength={6} value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} className="mt-1" data-testid="pwd-next" /></div>
            <Button type="submit" className="rounded-full" data-testid="pwd-submit">Şifreyi Değiştir</Button>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MembershipTab() {
  const [membership, setMembership] = useState(null);
  const [fee, setFee] = useState(null);
  const [payments, setPayments] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);

  const reload = async () => {
    const [m, f, p, b] = await Promise.all([
      api.get("/provider/membership").then((r) => r.data).catch(() => null),
      api.get("/provider/membership/fee").then((r) => r.data.amount).catch(() => null),
      api.get("/provider/payments").then((r) => r.data).catch(() => []),
      api.get("/bank-accounts/active").then((r) => r.data).catch(() => []),
    ]);
    setMembership(m); setFee(f); setPayments(p); setBanks(b);
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      try { await reload(); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  if (loading) return <div className="text-sm text-muted-foreground">Yükleniyor…</div>;

  const statusLabel = { active: "Aktif", expired: "Süresi Doldu", none: "Üye Değil" }[membership?.status || "none"];
  const statusClr = membership?.status === "active" ? "bg-[hsl(var(--success))] text-white" : membership?.status === "expired" ? "bg-destructive text-white" : "bg-slate-500 text-white";

  const methodLabel = (m) => ({ bank_transfer: "Banka Transferi", credit_card: "Kredi Kartı", manual: "Manuel/Admin" }[m] || m);

  return (
    <div className="space-y-4" data-testid="membership-tab">
      <div className="rounded-2xl border p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-semibold">Üyelik Durumu</h3>
          <Badge className={statusClr} data-testid="mem-status">{statusLabel}</Badge>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-sm">
          <div><div className="text-xs text-muted-foreground">Başlangıç</div><div className="font-semibold">{membership?.started_at ? new Date(membership.started_at).toLocaleDateString("tr-TR") : "—"}</div></div>
          <div><div className="text-xs text-muted-foreground">Bitiş</div><div className="font-semibold">{membership?.expires_at ? new Date(membership.expires_at).toLocaleDateString("tr-TR") : "—"}</div></div>
          <div><div className="text-xs text-muted-foreground">Kalan Gün</div><div className="font-semibold" data-testid="mem-remaining">{membership?.remaining_days ?? 0} gün</div></div>
          <div><div className="text-xs text-muted-foreground">Yıllık Ücret (365 gün)</div><div className="font-semibold" data-testid="mem-fee">{fee != null ? `${Number(fee).toLocaleString("tr-TR")} ₺` : "—"}</div></div>
        </div>
        {membership?.status !== "active" && (
          <div className="mt-3 rounded-xl bg-amber-500/10 border border-amber-500/40 text-amber-900 p-3 text-sm">
            Üyeliğiniz aktif değil. Banka transferi yapıp aşağıdan dekontunuzu yükleyerek ödeme bildirimi oluşturabilirsiniz.
          </div>
        )}
        <div className="mt-3">
          <Button onClick={() => setShowReport((s) => !s)} disabled={!fee || banks.length === 0} data-testid="pay-report-toggle" className="rounded-full">
            {showReport ? "Kapat" : "ÖDEME BİLDİR / DEKONT YÜKLE"}
          </Button>
          {!fee && <span className="text-xs text-muted-foreground ml-2">Üyelik ücreti henüz belirlenmedi.</span>}
          {fee && banks.length === 0 && <span className="text-xs text-muted-foreground ml-2">Yol Hattı banka hesabı henüz tanımlanmadı.</span>}
        </div>
      </div>

      {showReport && fee != null && banks.length > 0 && (
        <PaymentReportForm fee={fee} banks={banks} onDone={() => { setShowReport(false); reload(); }} />
      )}

      <div className="rounded-2xl border p-4">
        <h3 className="font-semibold mb-2">Ödeme Yöntemleri</h3>
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="rounded-xl border p-3">
            <div className="font-semibold text-sm">Banka Havalesi / EFT</div>
            {banks.length === 0 ? (
              <div className="text-sm text-muted-foreground mt-1">Aktif banka hesabı henüz tanımlanmadı.</div>
            ) : (
              <div className="grid gap-2 mt-2">
                {banks.map((b) => (
                  <div key={b.id} className="text-sm rounded-lg bg-muted p-2" data-testid={`bank-${b.id}`}>
                    <div><b>{b.bank_name}</b> — {b.account_holder}</div>
                    <div className="font-mono text-xs">IBAN: {b.iban}</div>
                    {b.branch && <div className="text-xs">Şube/Hesap: {b.branch}</div>}
                    {b.note && <div className="text-xs italic">{b.note}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="rounded-xl border p-3 opacity-70">
            <div className="font-semibold text-sm">Kredi Kartı</div>
            <div className="text-xs text-muted-foreground mt-1">Yakında — çevrimiçi ödeme entegrasyonu hazırlanıyor.</div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border p-4">
        <h3 className="font-semibold mb-2">Ödeme Geçmişi</h3>
        {payments.length === 0 ? (
          <div className="text-sm text-muted-foreground">Henüz ödeme kaydı yok.</div>
        ) : (
          <div className="grid gap-2 text-sm">
            {payments.map((p) => (
              <div key={p.id} className="rounded-lg border p-2 flex items-center justify-between flex-wrap gap-2" data-testid={`pay-${p.id}`}>
                <div>
                  <div className="text-xs text-muted-foreground">{new Date(p.payment_date).toLocaleString("tr-TR")}</div>
                  <div><b>{Number(p.amount).toLocaleString("tr-TR")} ₺</b> · {methodLabel(p.payment_method)}</div>
                  {p.note && <div className="text-xs italic text-muted-foreground">{p.note}</div>}
                </div>
                <Badge className={p.status === "approved" ? "bg-[hsl(var(--success))] text-white" : p.status === "rejected" ? "bg-destructive text-white" : "bg-amber-500 text-white"} data-testid={`pay-badge-${p.id}`}>
                  {p.status === "approved" ? "Onaylı" : p.status === "rejected" ? "Reddedildi" : "Bekliyor"}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PaymentReportForm({ fee, banks, onDone }) {
  const [bank, setBank] = useState(banks[0]?.id || "");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [ref, setRef] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!bank) { toast.error("Banka hesabı seçiniz"); return; }
    setSaving(true);
    try {
      let receipt_path = "";
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        receipt_path = up.data.path;
      }
      await api.post("/provider/payments/report", {
        bank_account_id: bank,
        payment_date: new Date(date).toISOString(),
        reference: ref,
        note,
        receipt_path,
      });
      toast.success("Ödeme bildiriminiz alındı. Admin onayı sonrası üyeliğiniz başlayacak/uzayacak.");
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border p-4" data-testid="payment-report-form">
      <h3 className="font-semibold mb-2">Ödeme Bildir / Dekont Yükle</h3>
      <div className="grid gap-2 sm:grid-cols-2 text-sm">
        <div>
          <div className="text-xs text-muted-foreground mb-1">Ödenen Tutar (sabit)</div>
          <div className="h-10 rounded-lg bg-muted px-3 flex items-center font-bold" data-testid="report-fee">{Number(fee).toLocaleString("tr-TR")} ₺</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground mb-1">Banka Hesabı</div>
          <select value={bank} onChange={(e) => setBank(e.target.value)} className="h-10 rounded-lg border w-full px-2 bg-background" data-testid="report-bank">
            {banks.map((b) => <option key={b.id} value={b.id}>{b.bank_name} — {b.account_holder}</option>)}
          </select>
        </div>
        <div>
          <div className="text-xs text-muted-foreground mb-1">Ödeme Tarihi</div>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="report-date" />
        </div>
        <div>
          <div className="text-xs text-muted-foreground mb-1">Referans</div>
          <Input placeholder="Havale referans no (ops.)" value={ref} onChange={(e) => setRef(e.target.value)} data-testid="report-ref" />
        </div>
        <div className="sm:col-span-2">
          <div className="text-xs text-muted-foreground mb-1">Açıklama</div>
          <Input placeholder="Not (ops.)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="report-note" />
        </div>
        <div className="sm:col-span-2">
          <div className="text-xs text-muted-foreground mb-1">Dekont (görsel veya PDF)</div>
          <Input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="report-file" />
        </div>
      </div>
      <div className="mt-3">
        <Button onClick={submit} disabled={saving} data-testid="report-submit">
          {saving ? "Gönderiliyor…" : "Bildirimi Gönder"}
        </Button>
      </div>
    </div>
  );
}

function ProviderRevenueCards() {
  const [rev, setRev] = useState(null);
  useEffect(() => {
    api.get("/provider/revenue").then(({ data }) => setRev(data)).catch(() => setRev(null));
  }, []);
  const cells = [
    { key: "today", label: "BUGÜNKÜ CİRO" },
    { key: "month", label: "BU AY CİRO" },
    { key: "year", label: "BU YIL CİRO" },
    { key: "total", label: "TOPLAM CİRO" },
  ];
  return (
    <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="provider-revenue-cards">
      {cells.map((c) => {
        const cell = rev?.[c.key] || {};
        return (
          <div key={c.key} className="rounded-2xl border p-4">
            <div className="text-xs text-muted-foreground">{c.label}</div>
            <div className="text-2xl font-bold text-primary" data-testid={`rev-${c.key}`}>
              {Number(cell.revenue || 0).toLocaleString("tr-TR")} ₺
            </div>
            <div className="text-xs text-muted-foreground mt-1">{cell.job_count || 0} tamamlanan iş</div>
          </div>
        );
      })}
    </div>
  );
}

